'use client';

import { Workspace } from '@/types/pulse';
import { Interview } from '@/types/pulsedb';
import {
  useSupaClient,
  useUser,
  useWorkspace,
  useWorkspaces
} from '@/utils/clerk/client';
import { useEntitlements } from '@/utils/entitlements/client';
import { getRecentInterviews } from '@/utils/supabase/queries';
import { useClerk } from '@clerk/nextjs';
import {
  ClipboardListIcon,
  LucideIcon,
  MicIcon,
  UsersIcon
} from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { useState } from 'react';
import useSWR from 'swr';

export type PrimaryNavLink = {
  href: string;
  label: string;
  icon: LucideIcon;
};

export function useAccountNavData() {
  const { signOut, openUserProfile } = useClerk();

  const { user, isPaidUser } = useUser();
  const { workspaces } = useWorkspaces();
  const { workspace, isLoaded } = useWorkspace();
  const supaClient = useSupaClient();
  const entitlements = useEntitlements();
  const router = useRouter();
  const pathname = usePathname();

  const [createTeamOpen, setCreateTeamOpen] = useState(false);

  const inactiveWorkspaces = workspaces?.filter((w) => w.id !== workspace?.id);
  const teamCount = workspaces?.filter((w) => !w.personal).length ?? 0;

  const recentInterviewsKey =
    workspace && supaClient
      ? `/api/workspaces/${workspace.id}/recent-interviews`
      : null;

  const recentInterviewsFetcher = async (_key: string) => {
    if (!workspace || !supaClient) return [];
    return (await getRecentInterviews(supaClient, workspace.id)) || [];
  };

  const { data: recentInterviews = [] } = useSWR<Interview[]>(
    recentInterviewsKey,
    recentInterviewsFetcher
  );

  // Surveys and Respondents are entitlement-gated; Interviews is always
  // present, which is why it anchors the group and owns the recents below it.
  const primaryNav: PrimaryNavLink[] = [
    { href: '/account/interview', label: 'Interviews', icon: MicIcon },
    ...(entitlements.surveys.value
      ? [{ href: '/account/survey', label: 'Surveys', icon: ClipboardListIcon }]
      : []),
    ...(entitlements.respondents.value
      ? [
          {
            href: '/account/respondents',
            label: 'Respondents',
            icon: UsersIcon
          }
        ]
      : [])
  ];

  const activateWorkspace = (ws: Workspace) => {
    ws.setActive?.();
    router.push(`/account/`);
  };

  return {
    // User
    user,
    isPaidUser,
    signOut,
    openUserProfile,

    // Workspace
    workspace,
    workspaces,
    inactiveWorkspaces,
    teamCount,
    isLoaded,
    activateWorkspace,

    // Create team modal
    createTeamOpen,
    setCreateTeamOpen,

    // Navigation
    pathname,
    router,
    primaryNav,

    // Interviews
    recentInterviews
  };
}
